import {
  GetApiIntroducerPortalIntroducerApplicationOfferDrawDownRepaymentScheduleResponse,
  GetApiIntroducerPortalRepaymentSimulatorResponse,
} from '@iwoca/lapi-client/introducer_portal';
import { Table } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';

import styles from './RepaymentScheduleTable.module.css';

type TDrawdownCalculatorResponse =
  GetApiIntroducerPortalIntroducerApplicationOfferDrawDownRepaymentScheduleResponse['data'];

type TStandaloneCalculatorResponse =
  GetApiIntroducerPortalRepaymentSimulatorResponse['data'];

type TSchedule =
  | TDrawdownCalculatorResponse['schedule']
  | TStandaloneCalculatorResponse['schedule'];

type TProps = {
  schedule: TSchedule;
  summary:
    | TDrawdownCalculatorResponse['summary']
    | TStandaloneCalculatorResponse['summary'];
};

export const RepaymentScheduleTable = ({ schedule, summary }: TProps) => {
  const { i18n } = useLocale();
  const { formatCurrency } = useNumberFormat();

  return (
    <Table
      id="repayment-schedule"
      className={styles.repaymentSchedule}
      maxHeight={512}
      columns={[
        {
          accessorFn: (repayment: TSchedule[number]) => {
            return i18n.date(repayment.date, {
              day: '2-digit',
              month: 'numeric',
              year: 'numeric',
            });
          },
          header: t`Date`.toUpperCase(),
          footer: t`Total`,
        },
        {
          accessorFn: (repayment: TSchedule[number]) => {
            return formatCurrency(repayment.principal);
          },
          header: t`Original loan`.toUpperCase(),
          footer: () => {
            return formatCurrency(summary.total_credit_amount);
          },
        },
        {
          accessorFn: (repayment: TSchedule[number]) => {
            return formatCurrency(repayment.interest + repayment.total_fees);
          },
          header: t`Fees + interest`.toUpperCase(),
          footer: () => {
            return formatCurrency(
              (summary.total_interest || 0) + (summary.total_fees || 0),
            );
          },
        },
        {
          accessorFn: (repayment: TSchedule[number]) => {
            return formatCurrency(repayment.total);
          },
          header: t`Total`.toUpperCase(),
          footer: () => {
            return formatCurrency(summary.total_payable);
          },
        },
      ]}
      data={schedule}
      pinFirstCol={false}
    />
  );
};
